/**
 * Type contracts for the FCM Family Philanthropy Purpose Questionnaire — CUX-1841.
 *
 * The backend (`PhilanthropicProfile`) is the source of truth for question
 * text, options, ordering, and types. The FE renders whatever the server
 * sends and submits answers keyed by `question_code` — no hardcoded option
 * lists here.
 *
 * Section titles/descriptions are NOT yet in the payload, so they live on
 * the FE in `FCM_SECTION_META` until the backend exposes them.
 */

export type SubmissionType = 'solo' | 'couple';

export type SectionMode = 'guided' | 'madlibs';

export type FcmQuestionType =
  | 'textarea'
  | 'radio'
  | 'checkbox'
  | 'vehicle_card'
  | 'slider'
  | 'madlib';

export type ScaleValue = 1 | 2 | 3 | 4 | 5;

export interface FcmAnswerOption {
  id: number;
  /** Display label shown to the user. */
  label: string;
  /** Stable backend-stored value for this option (the wire format). */
  value: string;
  description: string | null;
  position: number;
}

export interface FcmQuestion {
  id: number;
  /** 1..5 = content sections, 6 = interstitial "additional notes". */
  section: number;
  position: number;
  question_type: FcmQuestionType;
  /** Stable backend column key — used as the form-control name. */
  question_code: string;
  label: string;
  hint: string | null;
  /** Only set for `madlib` questions — text with a single `___` placeholder. */
  madlib_template: string | null;
  answer_options: FcmAnswerOption[];
}

export interface FcmQuestionsResponse {
  household: { id: number; members_count: number };
  submission_types: SubmissionType[];
  question_types: FcmQuestionType[];
  total_questions: number;
  total_questions_answered: number;
  total_questions_unanswered: number;
  questions: FcmQuestion[];
}

/**
 * FE-side answers map — keyed by `question_code`, used for hydrating a
 * prior record on the result screen. Not the wire format.
 */
export interface FcmAnswersMap {
  [question_code: string]: string | string[] | number | null;
}

/**
 * One row of the submission payload.
 *
 *   - text-based questions (textarea, madlib, slider) → `text_value`
 *   - choice questions (radio, vehicle_card)          → `philanthropic_profile_answer_option_id`
 *   - multi-select (checkbox)                         → one row per selected option,
 *                                                       same question_id, distinct option_id
 */
export interface FcmResponseAttribute {
  philanthropic_profile_question_id: number;
  text_value?: string;
  philanthropic_profile_answer_option_id?: number;
}

/**
 * Wire format for `POST /philanthropic_profiles`. Backend persists each
 * row as a `PhilanthropicProfileResponse`, so multi-select questions
 * naturally produce multiple rows tied to the same question.
 */
export interface FcmSubmission {
  submission_type: SubmissionType;
  philanthropic_profile_responses_attributes: FcmResponseAttribute[];
}

/**
 * Record returned by GET /me and POST submit. Shape is the BE's choice;
 * `purpose_statement` is null while a couple submission waits on the
 * spouse — that drives the UI's waiting screen.
 */
export interface FcmRecord {
  id: string;
  submission_type: SubmissionType;
  answers: FcmAnswersMap;
  purpose_statement: string | null;
  spouse_submitted: boolean;
  submitted_at: string;
  updated_at: string;
}

// ───────────────────── FE-only section metadata ─────────────────────
//
// Section titles, eyebrows, and descriptions aren't yet returned by the
// backend payload. Keep them here so the rest of the UI stays decoupled.

export interface SectionMeta {
  /** Backend `section` number (1..6). */
  section: number;
  /** Small eyebrow above the title — e.g. "Section 1 of 5 — Origin & Motivation". */
  eyebrow: string;
  title: string;
  description: string;
}

export const FCM_SECTION_META: ReadonlyArray<SectionMeta> = [
  {
    section: 1,
    eyebrow: 'Section 1 of 5 — Origin & Motivation',
    title: 'Where does your generosity come from?',
    description:
      'Understanding your "why" is the foundation of a giving strategy that lasts. ' +
      'These questions help surface the experiences, people, and moments that shaped ' +
      'your relationship with giving.',
  },
  {
    section: 2,
    eyebrow: 'Section 2 of 5 — Core Values',
    title: 'What do you believe in?',
    description:
      'Values are the compass for your philanthropy. When giving aligns with deeply ' +
      'held beliefs, it becomes sustainable and fulfilling rather than transactional.',
  },
  {
    section: 3,
    eyebrow: 'Section 3 of 5 — Focus & Priorities',
    title: 'What challenges will you tackle?',
    description:
      'Focused philanthropy creates more lasting change than scattered giving. This ' +
      'section helps you narrow from what you care about broadly to what you want to ' +
      'act on specifically.',
  },
  {
    section: 4,
    eyebrow: 'Section 4 of 5 — Family & Legacy',
    title: 'Who gives with you, and who comes after?',
    description:
      'Philanthropy can be one of the most powerful ways to transmit values across ' +
      'generations and engage family around shared purpose.',
  },
  {
    section: 5,
    eyebrow: 'Section 5 of 5 — Strategy & Style',
    title: 'How do you want to give?',
    description:
      "Your giving strategy reflects not just what you believe but how involved you " +
      'want to be. These questions clarify your preferred role as a philanthropist.',
  },
];

export const MAX_CORE_VALUES = 5;
